import { SystemStyleObject, Tabs as ChakraTabs } from '@chakra-ui/react'
import { createContext, useContext } from 'react'

const SegmentedControlSizeContext = createContext<{
  tabSize: ChakraTabs.RootProps['size']
}>({
  tabSize: 'md',
})

export type SegmentedControlsRootProps = Omit<ChakraTabs.RootProps, 'variant'>

export const SegmentedControlsRoot = ({
  size = 'md',
  ...rest
}: SegmentedControlsRootProps) => {
  return (
    <SegmentedControlSizeContext.Provider value={{ tabSize: size }}>
      <ChakraTabs.Root variant="plain" size={size} {...rest} />
    </SegmentedControlSizeContext.Provider>
  )
}

export interface SegmentedControlsListProps
  extends Omit<ChakraTabs.ListProps, 'variant'> {
  variant?: 'solid' | 'soft'
}
export const SegmentedControlsList = ({
  ...rest
}: SegmentedControlsListProps) => {
  return <ChakraTabs.List borderRadius="base" {...rest} />
}

export interface SegmentedControlTriggerProps extends ChakraTabs.TriggerProps {
  variant: 'solid' | 'soft'
}

export const SegmentedControlsTrigger = ({
  variant,
  ...rest
}: SegmentedControlTriggerProps) => {
  const { tabSize } = useContext(SegmentedControlSizeContext)
  let height: string | undefined = undefined
  let fontSize: string | undefined = undefined
  switch (tabSize) {
    case 'sm':
      height = '28px'
      fontSize = 'sm'
      break
    case 'md':
      height = '40px'
      fontSize = 'md'
      break
    case 'lg':
      height = '52px'
      fontSize = 'lg'
      break
    default:
      break
  }

  const _selected: SystemStyleObject = {}
  const _hover: SystemStyleObject = {}
  switch (variant) {
    case 'solid':
      _selected.color = 'white'
      _selected.bgColor = 'colorPalette.500'
      _selected.borderRadius = 'base'
      _selected.fontWeight = 'semibold'
      _hover['&:not([aria-selected=true], [data-selected])'] = {
        bgColor: 'colorPalette.100',
        borderRadius: 'base',
      }
      break
    case 'soft':
      _selected.color = 'colorPalette.500'
      _selected.bgColor = 'colorPalette.200'
      _selected.borderRadius = 'base'
      _selected.fontWeight = 'semibold'
      _hover.bgColor = 'colorPalette.200'
      _hover.borderRadius = 'base'
      break
  }

  return (
    <ChakraTabs.Trigger
      h={height}
      minH={height}
      color="status.mono"
      bgColor="gray.50"
      fontSize={fontSize}
      _selected={_selected}
      _hover={_hover}
      css={{
        // 以下の場合に線を表示しない:
        // 1. 最初の要素である
        // 2. 自身がアクティブな要素である
        // 3. 直前の要素がアクティブである
        '&:not(:first-of-type):not([aria-selected=true]):not([data-selected]):not([aria-selected=true] + *):not([data-selected] + *)':
          {
            backgroundImage:
              'linear-gradient(to bottom, transparent 20%, var(--chakra-colors-gray-300) 20%, var(--chakra-colors-gray-300) 80%, transparent 80%)',
            backgroundSize: '1px 100%',
            backgroundPosition: 'left center',
            backgroundRepeat: 'no-repeat',
          },
      }}
      {...rest}
    />
  )
}

export const SegmentedControlsIndicator = ChakraTabs.Indicator

export const SegmentedControlsContent = ChakraTabs.Content

export const SegmentedControlsContentGroup = ChakraTabs.ContentGroup
